import type { Schema } from './schema.js';

// The provenance a reducer sees. `timestamp` is the effective stamp (stamp.ts): the client's
// creation time, clamped against the server's receipt time for peer events. `origin` is
// 'local' for an own event folded at commit and 'remote' for anything folded from the log
// afterwards.
export interface EventMeta {
  readonly timestamp: number;
  readonly clientId: string;
  readonly userId: string | undefined;
  readonly origin: 'local' | 'remote';
}

export interface UndoEvent {
  readonly kind: string;
  readonly payload: unknown;
}

// One event kind. `precondition` is gate one: false means the event never exists. `apply`
// mutates the draft and reports what it did; `undefined` is gate two: the event is logged and
// synced but changed nothing here, so no undo entry and no notification entry. `undo` sees
// only that report (slide 23: "We don't use the original event here!"), which is what makes
// the inverse unable to disagree with the apply.
export interface EventDef<TState, TPayload, TResult> {
  readonly payload: Schema<TPayload>;
  precondition?(state: Readonly<TState>, payload: TPayload): boolean;
  apply(state: TState, payload: TPayload, meta: EventMeta): TResult | undefined;
  undo?(result: TResult): UndoEvent | undefined;
  // Online mode only: false drops the expected_aggregate_version guard on this event's write.
  readonly occ?: boolean | undefined;
  readonly typeMajor?: number | undefined;
  readonly typeMinor?: number | undefined;
  // Per-event confidentiality class; defaults to the aggregate's.
  readonly encrypt?: boolean | undefined;
}

// `any` in the payload and result positions escapes the variance trap: EventDef is
// contravariant in TPayload, so a literal event is not a subtype of EventDef<S, unknown, _>.
/* eslint-disable @typescript-eslint/no-explicit-any */
export type AnyEventDef<TState> = EventDef<TState, any, any>;
export type EventMap<TState> = { readonly [kind: string]: AnyEventDef<TState> };

export type PayloadOf<E> = E extends EventDef<any, infer P, any> ? P : never;
export type EventResultOf<E> = E extends EventDef<any, any, infer R> ? R : never;

export type MutationOf<TEvents extends EventMap<any>> = {
  [K in keyof TEvents & string]: { kind: K; payload: PayloadOf<TEvents[K]> };
}[keyof TEvents & string];

// The union of every event's result type, so a store can `switch (result.kind)` exhaustively.
export type ResultOf<TEvents extends EventMap<any>> = {
  [K in keyof TEvents & string]: EventResultOf<TEvents[K]>;
}[keyof TEvents & string];
/* eslint-enable @typescript-eslint/no-explicit-any */

// A retired kind kept foldable but not committable. History still contains it, so replay folds
// it; it is gone from `events`, so it cannot be committed and carries no schema.
export interface LegacyEventDef<TState> {
  apply(state: TState, payload: unknown, meta: EventMeta): unknown;
}

export type AggregateMode = 'local-first' | 'online';

export interface AggregateConfig<TState, TEvents extends EventMap<TState>> {
  readonly name: string;
  readonly mode: AggregateMode;
  readonly state: TState;
  readonly events: TEvents;
  readonly encrypt?: boolean | undefined;
  readonly schemaVersion?: number | undefined;
  readonly migrate?: ((prev: unknown, prevVersion: number) => TState) | undefined;
  readonly legacyEvents?: Record<string, LegacyEventDef<TState>> | undefined;
}

export interface WireType {
  readonly major: number;
  readonly minor: number;
}

export interface AggregateDef<TState, TEvents extends EventMap<TState>> {
  readonly name: string;
  readonly initialState: TState;
  readonly events: TEvents;
  readonly mode: AggregateMode;
  readonly encrypt: boolean;
  readonly schemaVersion: number;
  readonly migrate: ((prev: unknown, prevVersion: number) => TState) | undefined;
  readonly legacyEvents: Record<string, LegacyEventDef<TState>>;
  readonly wireTypes: Record<string, WireType>;
  // Per kind, resolved against the aggregate default.
  readonly encryptKinds: Record<string, boolean>;
  readonly _phantomState: TState;
  readonly _phantomMutation: MutationOf<TEvents>;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export type AggregateState<D> = D extends AggregateDef<infer S, any> ? S : never;
export type AggregateMutation<D> = D extends AggregateDef<any, infer E> ? MutationOf<E> : never;
export type AggregateKind<D> = D extends AggregateDef<any, infer E> ? keyof E & string : never;
export type AggregatePayload<D, K extends string> = D extends AggregateDef<any, infer E>
  ? K extends keyof E ? PayloadOf<E[K]> : never
  : never;
export type AggregateEvents<D> = D extends AggregateDef<any, infer E> ? E : never;
export type AggregateResult<D> = D extends AggregateDef<any, infer E> ? ResultOf<E> : never;
/* eslint-enable @typescript-eslint/no-explicit-any */

const KINDS_MAX = 4096;

export function defineAggregate<TState, TEvents extends EventMap<TState>>(
  config: AggregateConfig<TState, TEvents>,
): AggregateDef<TState, TEvents> {
  if (config.name.length === 0) throw new Error('aggregate name must not be empty');
  // immer needs a base object; a primitive state fails later inside produce with a worse message.
  if (config.state === null || typeof config.state !== 'object') {
    throw new Error(`aggregate "${config.name}" state must be a plain object`);
  }
  const mode: AggregateMode = config.mode;
  if (mode !== 'local-first' && mode !== 'online') {
    throw new Error(`aggregate "${config.name}": mode must be 'local-first' or 'online' (got ${String(mode)})`);
  }
  const kinds = Object.entries(config.events);
  if (kinds.length > KINDS_MAX) throw new Error(`aggregate "${config.name}": more than ${KINDS_MAX} event kinds`);
  for (const [kind, def] of kinds) {
    if (kind.length === 0) throw new Error('event kind must not be empty');
    if (typeof def.apply !== 'function') throw new Error(`event ${kind}: apply must be a function`);
    if (def.payload === undefined) throw new Error(`event ${kind}: payload schema is required`);
    // Online state moves only through server folds; there is no local apply to reverse.
    if (mode === 'online' && def.undo !== undefined) {
      throw new Error(`aggregate "${config.name}": event "${kind}" declares undo, but online-mode aggregates have no undo (remove it)`);
    }
    if (def.occ === false) {
      // Local-first writes carry no version guard, so there is nothing for the flag to switch off.
      if (mode === 'local-first') {
        throw new Error(`aggregate "${config.name}": event "${kind}" declares occ:false, but local-first aggregates have no OCC write path for it to switch off (remove it)`);
      }
      // Without the guard the state can move between the check and the commit, so the check
      // proves nothing it claims to.
      if (def.precondition !== undefined) {
        throw new Error(`aggregate "${config.name}": event "${kind}" declares occ:false AND a precondition; a precondition checked without a version guard proves nothing (the state can move between the check and the commit); drop one`);
      }
    }
  }
  const legacyEvents = config.legacyEvents ?? {};
  for (const kind of Object.keys(legacyEvents)) {
    if (Object.prototype.hasOwnProperty.call(config.events, kind)) {
      throw new Error(`aggregate "${config.name}": "${kind}" is in both events and legacyEvents`);
    }
  }
  const encrypt = config.encrypt ?? false;
  const wireTypes: Record<string, WireType> = {};
  const encryptKinds: Record<string, boolean> = {};
  for (const [kind, def] of kinds) {
    wireTypes[kind] = { major: def.typeMajor ?? hashKindName(kind), minor: def.typeMinor ?? 1 };
    encryptKinds[kind] = def.encrypt ?? encrypt;
  }
  return {
    name: config.name,
    initialState: config.state,
    events: config.events,
    mode,
    encrypt,
    schemaVersion: config.schemaVersion ?? 1,
    migrate: config.migrate,
    legacyEvents,
    wireTypes,
    encryptKinds,
    _phantomState: undefined as unknown as TState,
    _phantomMutation: undefined as unknown as MutationOf<TEvents>,
  };
}

// djb2 clamped to 31 bits so it fits an i32 wire field without flipping sign. Two builds with
// the same kind name must agree on the major.
function hashKindName(kind: string): number {
  let h = 5381;
  for (let i = 0; i < kind.length; i++) h = ((h << 5) + h + kind.charCodeAt(i)) | 0;
  return h & 0x7fffffff;
}
